import {Button} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, SearchLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {useMemo, useState} from 'react';

import type {ContextEngineProgressStep} from './workstreamContextEngineChatApi';

const THINKING_BODY_MAX_HEIGHT_PX = 200;

function stepHasDescription(step: ContextEngineProgressStep): boolean {
  return Boolean(step.description?.trim());
}

/**
 * Steps with a description appear in the timeline. Steps without a description are header-only
 * (their message is shown in the streaming hint, not duplicated in the list).
 */
export function historyThinkingSteps(
  steps: ContextEngineProgressStep[]
): ContextEngineProgressStep[] {
  return steps.filter(stepHasDescription);
}

function streamingTitleMessageFromSteps(steps: ContextEngineProgressStep[]): string {
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const step = steps[i];
    if (!stepHasDescription(step)) {
      return (step?.message ?? '').trim();
    }
  }
  return '';
}

const ThinkingStepRow = ({step}: {step: ContextEngineProgressStep}) => {
  const [searchesOpen, setSearchesOpen] = useState(false);
  const searches = step.searches ?? [];
  const searchCount = searches.length;

  return (
    <Box position="relative" paddingLeft="20" paddingBottom="16" style={{minWidth: 0}}>
      <Box
        position="absolute"
        style={{
          left: '3px',
          top: '7px',
          width: '8px',
          height: '8px',
        }}
        borderRadius="XXLarge"
        backgroundColor="Border Base"
      />
      <Stack gap="4" style={{minWidth: 0}}>
        <Text size="small" isBold>
          {step.message}
        </Text>
        {step.description?.trim() ? (
          <Box>
            <Text color="subtle" size="xsmall">
              {step.description.trim()}
            </Text>
          </Box>
        ) : null}
        {searchCount > 0 ? (
          <Box paddingTop="2">
            <Chip
              type="button"
              variant="standard"
              size="small"
              onClick={() => setSearchesOpen((o) => !o)}
              aria-expanded={searchesOpen}
            >
              <Chip.IconAccessory>
                <UIIcon src={SearchLine} size="small" className={atoms({color: 'Text Subtle'})} />
              </Chip.IconAccessory>
              <Chip.Content>
                <Box display="flex" alignItems="center" style={{gap: 6}}>
                  <Text size="xsmall">
                    {t('workstream_chat_thinking_searches', {count: searchCount})}
                  </Text>
                  <UIIcon
                    src={searchesOpen ? ChevronDownLine : ChevronRightLine}
                    size="small"
                    className={atoms({color: 'Text Subtle'})}
                  />
                </Box>
              </Chip.Content>
            </Chip>
            {searchesOpen ? (
              <Stack gap="6" paddingTop="8">
                {searches.map((s, i) => (
                  <Box
                    key={`${s.query}-${i}`}
                    paddingX="10"
                    paddingY="8"
                    borderRadius="Large"
                    borderStyle="Solid"
                    borderColor="Border Subtle"
                    borderWidth="1"
                    backgroundColor="Background Base"
                    style={{minWidth: 0}}
                  >
                    <Box display="flex" alignItems="flex-start" style={{gap: 4}}>
                      <UIIcon
                        src={SearchLine}
                        size="small"
                        className={atoms({color: 'Text Subtle', flexShrink: 0})}
                      />
                      <Text size="xsmall">{s.query}</Text>
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export const WorkstreamContextEngineThoughtSecondsCaption = ({
  thoughtSeconds,
  withBelowSpacing,
}: {
  thoughtSeconds: number;
  withBelowSpacing?: boolean;
}) => (
  <Box
    width="100%"
    marginBottom={withBelowSpacing ? '8' : undefined}
    style={{maxWidth: 'min(520px, 85vw)', minWidth: 0}}
  >
    <Box display="flex" alignItems="center" style={{gap: 4, minWidth: 0}}>
      <Text size="small" color="subtle" style={{flex: '1 1 auto', minWidth: 0}}>
        {t('workstream_chat_thought_seconds', {count: thoughtSeconds})}
      </Text>
    </Box>
  </Box>
);

export const WorkstreamContextEngineThinkingPanel = ({
  steps,
  expanded,
  onToggleExpanded,
  streaming,
  thoughtSeconds,
  withBelowSpacing,
}: {
  steps: ContextEngineProgressStep[];
  expanded: boolean;
  onToggleExpanded: () => void;
  streaming: boolean;
  /** Elapsed think time for a completed turn; used when there are no expandable timeline steps. */
  thoughtSeconds?: number | null;
  /** Extra space under the panel when embedded above an assistant bubble (streaming strip omits this). */
  withBelowSpacing?: boolean;
}) => {
  const historySteps = useMemo(() => historyThinkingSteps(steps), [steps]);
  const hasBody = historySteps.length > 0;

  const streamingHeaderHint = useMemo(() => streamingTitleMessageFromSteps(steps), [steps]);

  if (!streaming && !hasBody) {
    if (thoughtSeconds == null) {
      return null;
    }
    return (
      <WorkstreamContextEngineThoughtSecondsCaption
        thoughtSeconds={thoughtSeconds}
        withBelowSpacing={withBelowSpacing}
      />
    );
  }

  return (
    <Box
      width="100%"
      marginBottom={withBelowSpacing ? '8' : undefined}
      style={{maxWidth: 'min(520px, 85vw)', minWidth: 0}}
    >
      <Box display="flex" alignItems="center" style={{gap: 8}}>
        {streaming ? <Spinner size="xsmall" /> : null}
        <Button
          type="button"
          variant="transparent"
          hasUnderline={false}
          size="small"
          onClick={hasBody ? onToggleExpanded : undefined}
          {...(hasBody ? {'aria-expanded': expanded} : {})}
          style={{cursor: hasBody ? 'pointer' : 'default'}}
          tabIndex={hasBody ? undefined : -1}
        >
          <Box display="flex" alignItems="center" style={{gap: 4}}>
            <Text size="small" color="subtle" style={{flex: '1 1 auto', minWidth: 0}}>
              {streaming
                ? streamingHeaderHint || t('workstream_chat_thinking')
                : t('workstream_chat_show_thinking')}
            </Text>
            {hasBody ? (
              <UIIcon
                src={expanded ? ChevronDownLine : ChevronRightLine}
                size="small"
                className={atoms({color: 'Text Subtle'})}
                style={{flexShrink: 0}}
                aria-hidden
              />
            ) : null}
          </Box>
        </Button>
      </Box>
      {expanded && hasBody ? (
        <Box
          marginTop="8"
          paddingRight="4"
          overflowY="auto"
          minHeight="0"
          minWidth="0"
          style={{
            maxHeight: THINKING_BODY_MAX_HEIGHT_PX,
          }}
        >
          {historySteps.length > 0 ? (
            <Box position="relative" style={{minWidth: 0}}>
              <Box
                position="absolute"
                style={{
                  left: '6px',
                  top: '4px',
                  bottom: '4px',
                  width: '1px',
                }}
                backgroundColor="Border Subtle"
              />
              <Box>
                {historySteps.map((step, i) => (
                  <ThinkingStepRow
                    key={`${step.timestamp ?? ''}-${step.message}-${i}`}
                    step={step}
                  />
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
